import * as React from 'react';
import { CSSObject, SerializedStyles } from '@emotion/react';
import { HoneycombStyleSystemProps } from '../../theming/types/HoneycombStyleSystem';
import { useSystemStyles } from './style-system/useSystemStyles';
import { extractStyleSystemProps } from './style-system/extractSystemStyles';
import { ButtonBase } from '../ButtonBase/ButtonBase';
import { useCustomStyleProps } from './style-system/useCustomStyleProps';
import { FixMeLater } from '../../utils';

export interface StylerBaseProps extends HoneycombStyleSystemProps {
    /**
     * Children.
     */
    children?: React.ReactNode;

    /**
     * The root element.
     * @default div
     */
    component?: React.ElementType;

    /**
     * If `true`, the component will use `ButtonBase` and have role="button".
     * Use this if you want to apply an `onClick` handler.
     * @default false
     */
    button?: boolean;

    /**
     * This property can be used to augment the component with additional theme specific style properties
     * which have been specified on the theme's `customStyleProps` object.
     * @default false
     */
    styleId?: string;

    /**
     * Permit additional properties which will be passed to the root node.
     */
    [x: string]: unknown;

    /**
     * A reference passed to the root element.
     */
    ref?: React.Ref<FixMeLater>;
}

export interface StylerProps extends StylerBaseProps {
    baseStyles?: CSSObject | SerializedStyles | Array<SerializedStyles | CSSObject | null>;
}

export function Styler(props: Readonly<StylerProps>): React.JSX.Element {
    const { baseStyles, component: componentProp = 'div', button, styleId, ref, ...other } = props;
    const { styleSystemProps, rest } = extractStyleSystemProps(other);

    // If the component has a styleId then we may have custom style props which need to be
    // merged with our system style props.
    const customStyleProps = useCustomStyleProps(styleId);

    const styleProps = customStyleProps ? { ...styleSystemProps, ...customStyleProps } : styleSystemProps;

    const systemStyles = useSystemStyles(styleProps);

    const Component = button ? ButtonBase : componentProp;
    const ComponentProps = button ? { component: componentProp } : undefined;

    return <Component ref={ref} css={[baseStyles, systemStyles]} {...ComponentProps} {...rest} />;
}
